'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { m, useReducedMotion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { useConsent } from '@/context/ConsentContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ACCEPT_ALL, NECESSARY_ONLY } from '@/lib/consent';

interface RowDef {
    category: 'analytics' | 'marketing';
    labelKey: string;
    descKey: string;
}

const ROWS: RowDef[] = [
    { category: 'analytics', labelKey: 'cookie.prefs.analytics', descKey: 'cookie.prefs.analytics.desc' },
    { category: 'marketing', labelKey: 'cookie.prefs.marketing', descKey: 'cookie.prefs.marketing.desc' },
];

function Switch({
    checked,
    onChange,
    disabled,
    ariaLabel,
    describedby,
}: {
    checked: boolean;
    onChange?: () => void;
    disabled?: boolean;
    ariaLabel: string;
    describedby?: string;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={ariaLabel}
            aria-describedby={describedby}
            disabled={disabled}
            onClick={onChange}
            className={cn(
                'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                checked ? 'bg-primary' : 'bg-muted',
                disabled && 'cursor-not-allowed opacity-60',
            )}
        >
            <span
                className={cn(
                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                    checked ? 'translate-x-6 rtl:-translate-x-6' : 'translate-x-1 rtl:-translate-x-1',
                )}
            />
        </button>
    );
}

export default function CookiePreferencesModal() {
    const { t, isRTL } = useLanguage();
    const { consent, decide, closePreferences } = useConsent();
    const reduceMotion = useReducedMotion();

    const [enabled, setEnabled] = useState<Record<'analytics' | 'marketing', boolean>>({
        analytics: consent?.categories.analytics ?? false,
        marketing: consent?.categories.marketing ?? false,
    });

    const dialogRef = useRef<HTMLDivElement>(null);
    const restoreFocusRef = useRef<HTMLElement | null>(null);

    // Focus management, scroll lock and focus restore on mount/unmount.
    useEffect(() => {
        restoreFocusRef.current = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            restoreFocusRef.current?.focus?.();
        };
    }, []);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closePreferences();
                return;
            }
            if (e.key !== 'Tab') return;
            const root = dialogRef.current;
            if (!root) return;
            const focusables = root.querySelectorAll<HTMLElement>(
                'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        },
        [closePreferences],
    );

    const toggle = (cat: 'analytics' | 'marketing') =>
        setEnabled((prev) => ({ ...prev, [cat]: !prev[cat] }));

    const save = () =>
        decide({ necessary: true, analytics: enabled.analytics, marketing: enabled.marketing });

    const duration = reduceMotion ? 0 : 0.3;

    return (
        <div className="fixed inset-0 z-[9999] flex items-end justify-center sm:items-center sm:p-4">
            {/* Backdrop */}
            <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={closePreferences}
                aria-hidden="true"
            />

            {/* Panel */}
            <m.div
                ref={dialogRef}
                tabIndex={-1}
                dir={isRTL ? 'rtl' : 'ltr'}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cookie-prefs-title"
                onKeyDown={onKeyDown}
                initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
                transition={{ duration, ease: 'easeOut' }}
                className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-2xl border border-border bg-card p-6 text-card-foreground shadow-xl focus:outline-none sm:max-w-lg sm:rounded-2xl"
            >
                <h2 id="cookie-prefs-title" className="text-lg font-bold text-foreground">
                    {t('cookie.prefs.title')}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">{t('cookie.prefs.description')}</p>

                {/* Necessary — always on, non-disableable */}
                <div className="mt-5 flex items-start justify-between gap-4 rounded-xl border border-border p-4">
                    <div>
                        <p className="text-sm font-semibold text-foreground">{t('cookie.prefs.necessary')}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{t('cookie.prefs.necessary.desc')}</p>
                        <p id="cookie-necessary-always" className="mt-1 text-xs font-medium text-primary">
                            {t('cookie.prefs.alwaysActive')}
                        </p>
                    </div>
                    <Switch checked disabled ariaLabel={t('cookie.prefs.necessary')} describedby="cookie-necessary-always" />
                </div>

                {ROWS.map((row) => (
                    <div
                        key={row.category}
                        className="mt-3 flex items-start justify-between gap-4 rounded-xl border border-border p-4"
                    >
                        <div>
                            <p className="text-sm font-semibold text-foreground">{t(row.labelKey)}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{t(row.descKey)}</p>
                        </div>
                        <Switch
                            checked={enabled[row.category]}
                            onChange={() => toggle(row.category)}
                            ariaLabel={t(row.labelKey)}
                        />
                    </div>
                ))}

                <div className={cn('mt-6 flex flex-col gap-3 sm:flex-row', isRTL && 'sm:flex-row-reverse')}>
                    <Button variant="glow" onClick={save} className="flex-1">
                        {t('cookie.prefs.save')}
                    </Button>
                    <Button variant="outline" onClick={() => decide(ACCEPT_ALL)} className="flex-1">
                        {t('cookie.prefs.acceptAll')}
                    </Button>
                    <Button variant="ghost" onClick={() => decide(NECESSARY_ONLY)} className="flex-1">
                        {t('cookie.prefs.rejectAll')}
                    </Button>
                </div>
                <button
                    type="button"
                    onClick={closePreferences}
                    className="mt-3 w-full text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                    {t('cookie.prefs.cancel')}
                </button>
            </m.div>
        </div>
    );
}
